import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Chip,
  Autocomplete,
  TextField,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import api from "../../globals/axios";
import { useAlert } from "../Alert";
import { useTranslation } from "react-i18next";
import { isNotNullOrEmptyArray } from "../../globals/utils";
import { languages } from "../../globals/utils";

const WeatherSubscriptions = () => {
  const theme = useTheme();
  const [districts, setDistricts] = useState([]);
  const [selectedDistricts, setSelectedDistricts] = useState([]);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const { showAlert } = useAlert();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    fetchDistricts();
    fetchSubscription();
  }, []);

  const fetchDistricts = async () => {
    try {
      const response = await api.get("/alerts/districts/");
      if (response?.data?.data?.districts) {
        const districts = response.data.data.districts;
        setDistricts(districts?.map((d) => d.district) || []);
      }
    } catch (err) {
      console.error("Error loading districts:", err);
      showAlert(
        "error",
        t("Failed to load districts. Please try again later!")
      );
    }
  };

  const fetchSubscription = async () => {
    try {
      const { data } = await api.get("/alerts/weather-subscription");
      const subscribed = data?.data?.subscribed_districts;
      if (subscribed) {
        setSelectedDistricts(subscribed.split(",").map((d) => d.trim()));
      }

      const langCodes = data?.data?.subscribed_languages;
      if (langCodes) {
        const codes = langCodes.split(",").map((c) => c.trim());
        const matchedLanguages = languages.filter((lang) =>
          codes.includes(lang.value)
        );
        setSelectedLanguages(matchedLanguages);
      }
    } catch (err) {
      console.error("Error loading subscription:", err);
      showAlert("error", t("Failed to load existing subscriptions"));
    }
  };

  const saveSubscription = async () => {
    setSubmitted(true);
    if (
      !isNotNullOrEmptyArray(selectedDistricts) ||
      !isNotNullOrEmptyArray(selectedLanguages)
    ) {
      showAlert("error", t("Failed to save subscription."));
      return;
    }
    try {
      await api.post("/alerts/weather-subscription/", {
        subscribed_districts: selectedDistricts.join(","),
        subscribed_languages: selectedLanguages
          .map((lang) => lang.value)
          .join(","),
      });
      setSubmitted(false);
      showAlert("success", t("Subscription saved!"));
    } catch (err) {
      console.error("Save failed:", err);
      showAlert("error", t("Failed to save subscription."));
    }
  };

  return (
    <Box>
      <Typography
        variant={isMobile ? "h6" : "h5"}
        sx={{ fontWeight: theme.typography.fontWeightMedium, mb: 2 }}
      >
        {t("Weather Alert Subscriptions")}
      </Typography>

      {isNotNullOrEmptyArray(selectedDistricts) && (
        <Box mt={2} mb={1}>
          <Typography variant="subtitle2" mb={1}>
            {t("Selected Districts")} ({selectedDistricts.length}):
          </Typography>
          {selectedDistricts.map((district, i) => (
            <Chip
              key={i}
              label={district}
              onDelete={() =>
                setSelectedDistricts((prev) =>
                  prev.filter((_, index) => index !== i)
                )
              }
              sx={{ mr: 1, mb: 1 }}
            />
          ))}
        </Box>
      )}

      <Autocomplete
        multiple
        options={districts}
        value={selectedDistricts}
        onChange={(_, newValue) => setSelectedDistricts(newValue || [])}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t("Select Districts")}
            placeholder={t("Search districts...")}
            required
            error={submitted && !isNotNullOrEmptyArray(selectedDistricts)}
            helperText={
              submitted && !isNotNullOrEmptyArray(selectedDistricts)
                ? t("This field is required")
                : ""
            }
          />
        )}
        renderTags={() => null}
        sx={{ mb: 1, mt: 1 }}
        disabled={!isNotNullOrEmptyArray(districts)}
      />

      {isNotNullOrEmptyArray(selectedLanguages) && (
        <Box mt={2}>
          <Typography variant="subtitle2" mb={1}>
            {t("Selected Languages")} ({selectedLanguages.length}):
          </Typography>
          {selectedLanguages.map((lang, i) => (
            <Chip
              key={i}
              label={lang.label}
              onDelete={() =>
                setSelectedLanguages((prev) =>
                  prev.filter((_, index) => index !== i)
                )
              }
              sx={{ mr: 1, mb: 1 }}
            />
          ))}
        </Box>
      )}

      <Autocomplete
        multiple
        options={languages}
        getOptionLabel={(option) => option.label}
        value={selectedLanguages}
        onChange={(_, newValue) => setSelectedLanguages(newValue || [])}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t("Select Languages")}
            placeholder={t("Search languages...")}
            sx={{ mb: 2, mt: 1 }}
            required
            error={submitted && !isNotNullOrEmptyArray(selectedLanguages)}
            helperText={
              submitted && !isNotNullOrEmptyArray(selectedLanguages)
                ? t("This field is required")
                : ""
            }
          />
        )}
        renderTags={() => null}
        disableCloseOnSelect
      />

      <Button onClick={saveSubscription} variant="contained" sx={{ mt: 1 }}>
        {t("Save Subscription")}
      </Button>
    </Box>
  );
};

export default WeatherSubscriptions;
